import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { coordinate } from '@/lib/format';
import { useReverseGeocode } from '@/lib/queries/geocoding';
import { cn } from '@/lib/utils';

/**
 * A named location with its coordinate beneath it, and the provider's full
 * address on hover.
 *
 * The name is looked up on demand and may never arrive — an unnamed spot, a
 * geocoder that is down, or the feature switched off — so the coordinate is
 * always rendered and the name is purely additive. A vehicle that has never
 * reported has no position to name at all.
 */
export function PlaceLine({
  lat,
  lng,
  className,
}: {
  lat: number | null | undefined;
  lng: number | null | undefined;
  className?: string;
}) {
  const hasPoint = lat !== null && lat !== undefined && lng !== null && lng !== undefined;
  const { data: place, isLoading } = useReverseGeocode(lat ?? undefined, lng ?? undefined);

  if (!hasPoint) {
    return <p className={cn('tnum text-[13px]', className)}>{coordinate(lat, lng)}</p>;
  }

  if (isLoading) return <Skeleton className="mt-0.5 h-4 w-44" />;

  if (!place) {
    return <p className={cn('tnum text-[13px]', className)}>{coordinate(lat, lng)}</p>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-default">
          <p className={cn('text-[13px] leading-tight', className)}>{place.label}</p>
          <p className="tnum text-[11px] text-muted-foreground">{coordinate(lat, lng)}</p>
        </div>
      </TooltipTrigger>
      {place.full && <TooltipContent className="max-w-[280px]">{place.full}</TooltipContent>}
    </Tooltip>
  );
}
